<template>
  <div class="bg p-3 pb-20 mb-auto h-full overflow-auto text-white" id="style-1">
    <!-- Input and Selection Section -->
    <div class="text-center mb-6">
      <h2 class="text-lg sm:text-xl font-semibold text-indigo-300 mb-3 font-orbitron animate-fadeIn">
        ตรวจสอบแคมป์และชาวบ้าน
      </h2>
      <div class="flex flex-col sm:flex-row justify-center items-center gap-3">
        <div class="flex items-center gap-3">
          <input
            v-model="uid"
            type="text"
            placeholder="กรอก UID ผู้เล่น"
            class="p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
          />
          <button
            @click="fetchCampData"
            :disabled="isLoading"
            class="btn btn-primary"
            :class="{ 'cursor-not-allowed opacity-70': isLoading }"
          >
            <div class="btn-content">
              <i class="fas fa-search text-base"></i>
              <span class="text-xs font-semibold">ค้นหา</span>
            </div>
          </button>
        </div>
        <div class="flex items-center gap-3">
          <select
            v-model="selectedCharacter"
            class="bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600 w-64"
            @change="fetchCampDataByCharacter"
          >
            <option value="">เลือกตัวละครจากอันดับ</option>
            <option v-for="(survivor, index) in survivors" :key="survivor.docId" :value="survivor.docId">
              #{{ index + 1 }} {{ survivor.characterName || 'ไม่ทราบ' }}
            </option>
          </select>
          <button
            @click="previousCharacter"
            :disabled="isLoading || currentIndex <= 0"
            class="btn btn-primary"
            :class="{ 'cursor-not-allowed opacity-70': isLoading || currentIndex <= 0 }"
          >
            <div class="btn-content">
              <i class="fas fa-arrow-left text-base"></i>
              <span class="text-xs font-semibold">ก่อนหน้า</span>
            </div>
          </button>
          <button
            @click="nextCharacter"
            :disabled="isLoading || currentIndex >= survivors.length - 1"
            class="btn btn-primary"
            :class="{ 'cursor-not-allowed opacity-70': isLoading || currentIndex >= survivors.length - 1 }"
          >
            <div class="btn-content">
              <i class="fas fa-arrow-right text-base"></i>
              <span class="text-xs font-semibold">ถัดไป</span>
            </div>
          </button>
        </div>
      </div>
      <div v-if="error" class="text-red-400 text-sm mt-3 animate-fadeIn">{{ error }}</div>
    </div>

    <!-- Camp Data Display -->
    <div
      v-if="campData"
      class="bg-gray-700 bg-opacity-90 rounded-lg p-5 border border-gray-600 animate-scaleIn mt-6"
    >
      <h3 class="text-lg font-semibold text-indigo-300 mb-4 font-orbitron">ข้อมูลแคมป์</h3>
      <!-- Camp Capacity Comparison -->
      <div class="mb-6">
        <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบจำนวนชาวบ้านในแคมป์</h4>
        <div class="text-xs">
          <p class="text-indigo-200">จำนวนชาวบ้านในแคมป์ (จาก camp.villagers): {{ villagerIds.length }}</p>
          <p class="text-indigo-200">Schema: V2 (object-map)</p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">จำนวนชาวบ้านในแคมป์ (จาก camps)</h5>
              <div class="text-indigo-200 space-y-1">
                <p>จำนวนปัจจุบัน: {{ campData.capacity }}</p>
                <p>ความจุสูงสุด: {{ campData.maxCapacity }}</p>
              </div>
            </div>
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">จำนวนที่คำนวณได้ (จากชาวบ้านที่ถูกต้อง)</h5>
              <div class="text-indigo-200 space-y-1">
                <p>จำนวน: {{ calculatedVillagerCount }}</p>
              </div>
            </div>
          </div>
          <p
            class="mt-3 text-sm"
            :class="isVillagerCountMatching ? 'text-green-400' : 'text-red-400'"
          >
            {{ isVillagerCountMatching ? 'จำนวนชาวบ้านตรงกัน' : 'จำนวนชาวบ้านไม่ตรงกัน' }}
          </p>
        </div>
      </div>
      <!-- Villager List -->
      <div class="mb-6">
        <h4 class="text-sm font-semibold text-indigo-300 mb-3">รายการชาวบ้าน</h4>
        <div v-if="hasDuplicates" class="flex items-center text-red-500 text-sm mb-4">
          <i class="fas fa-exclamation-triangle mr-1"></i>
          พบ ID ชาวบ้านซ้ำกัน!
          <button @click="fixDuplicates" class="ml-2 px-2 py-1 bg-red-600 hover:bg-red-500 text-white rounded text-xs">
            แก้ไขทันที
          </button>
        </div>
        <!-- Filter -->
        <div class="flex justify-end items-center mb-4">
          <label class="text-indigo-200 text-sm mr-2">เรียงรองตาม:</label>
          <select v-model="sortBy" class="bg-gray-700 text-indigo-200 px-3 py-1 rounded border border-gray-600 focus:outline-none focus:border-indigo-500 transition">
            <option value="level">เลเวล</option>
            <option value="attackDice">ลูกเต๋าโจมตี</option>
            <option value="defenseDice">ลูกเต๋าป้องกัน</option>
            <option value="stamina">ความอึด (sta)</option>
            <option value="str">พละกำลัง (str)</option>
            <option value="agi">ความคล่องตัว (agi)</option>
            <option value="int">สติปัญญา (int)</option>
            <option value="luck">โชค (luck)</option>
            <option value="defense">ป้องกัน (def)</option>
          </select>
        </div>
        <div v-if="villagers.length > 0" class="space-y-4">
          <!-- กริดการ์ดชาวบ้าน -->
          <div class="grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
            <div
              v-for="v in sortedVillagers"
              :key="v.id"
              class="group relative bg-gray-900 bg-opacity-60 rounded-2xl border border-gray-800 overflow-hidden hover:border-indigo-600 transition"
            >
              <!-- ส่วนหัว: ภาพ + ชื่อ + เลเวล -->
              <div class="p-3 flex items-center">
                <div class="relative">
                  <img
                    :src="v.img || v.template?.normalFace || 'default-villager.png'"
                    alt="Villager"
                    class="w-14 h-14 rounded-lg object-cover border-2 border-indigo-500 bg-gray-800"
                    @error="handleImageError"
                  />
                  <div
                    v-if="(v.level || v.template?.level)"
                    class="absolute -bottom-2 left-1/3 transform -translate-x-1/2 px-2 py-0.5 rounded text-xxs bg-indigo-600 text-white shadow"
                  >
                    {{ v.level ?? v.template?.level }}
                  </div>
                </div>
                <div class="ml-3 flex-1">
                  <div class="flex items-center justify-between">
                    <div class="font-semibold text-gray-100 truncate">
                      {{ v.name }} 
                    </div>
                  </div>
                  <div class="flex items-center space-x-1 px-1">
                    <!-- ชิปบุก -->
                    <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-red-900 opacity-80 text-white text-xxs border border-red-600">
                      <i class="fa-solid fa-dice text-red-300 text-xs"></i>
                      <span class="font-mono">6</span>
                    </div>
                    <!-- ชิปตั้งรับ -->
                    <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-blue-900 opacity-80 text-white text-xxs border border-blue-600">
                      <i class="fa-solid fa-dice text-blue-300 text-xs"></i>
                      <span class="font-mono">6</span>
                    </div>
                  </div>
                  <div class="text-xs text-gray-400 truncate" v-if="v.template?.note">
                    {{ v.template.note }}
                  </div>
                </div>
              </div>
              <!-- แท็บสเตตัสแบบย่อ -->
              <div class="px-3 pb-3 space-y-2">
                <!-- ชิปค่าอื่นๆ พร้อม upgradeStats -->
                <div class="grid grid-cols-3 gap-1">
                  <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                    sta: {{ getTotalStat(v,'stamina') }}
                    <span v-if="getUpgradeStat(v,'stamina') > 0" class="text-green-400">(+{{ getUpgradeStat(v,'stamina') }})</span>
                  </span>
                  <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                    str: {{ getTotalStat(v,'str') }}
                    <span v-if="getUpgradeStat(v,'str') > 0" class="text-green-400">(+{{ getUpgradeStat(v,'str') }})</span>
                  </span>
                  <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                    agi: {{ getTotalStat(v,'agi') }}
                    <span v-if="getUpgradeStat(v,'agi') > 0" class="text-green-400">(+{{ getUpgradeStat(v,'agi') }})</span>
                  </span>
                  <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                    int: {{ getTotalStat(v,'int') }}
                    <span v-if="getUpgradeStat(v,'int') > 0" class="text-green-400">(+{{ getUpgradeStat(v,'int') }})</span>
                  </span>
                  <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                    luck: {{ getTotalStat(v,'luck') }}
                    <span v-if="getUpgradeStat(v,'luck') > 0" class="text-green-400">(+{{ getUpgradeStat(v,'luck') }})</span>
                  </span>
                  <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                    def: {{ getTotalStat(v,'defense') }}
                    <span v-if="getUpgradeStat(v,'defense') > 0" class="text-green-400">(+{{ getUpgradeStat(v,'defense') }})</span>
                  </span>
                </div>
                <!-- แสดง Stat Points ที่เหลือ -->
                <div v-if="v.statPoints > 0" class="text-center">
                  <span class="px-3 py-1 rounded bg-yellow-900 text-yellow-200 text-xxs border border-yellow-600 inline-block">
                    <i class="fas fa-star mr-1"></i>Stat Points: {{ v.statPoints }}
                  </span>
                </div>
              </div>
              <div class="px-3 pb-3 flex items-center">
                <div v-if="remainingMs(v) > 0" class="flex items-center text-yellow-400 text-xxs">
                  <i class="fas fa-hourglass-half mr-1 animate-pulse"></i>
                  <span class="font-medium">สามารถบุกได้หลัง</span> {{ formatRemaining(remainingMs(v)) }}
                </div>
                <div v-else class="flex items-center text-green-400 text-xxs">
                  <i class="fas fa-check-circle mr-1"></i>
                  <span class="font-medium">พร้อมใช้งาน</span>
                </div>
                <button
                  v-if="remainingMs(v) > 0"
                  @click="resetCooldown(v)"
                  :disabled="isUpdating"
                  class="btn btn-primary ml-2 py-1 px-2 text-xs"
                  :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
                >
                  <div class="btn-content">
                    <i class="fas fa-sync-alt text-sm"></i>
                    <span class="text-xs font-semibold">รีเซ็ต Cooldown</span>
                  </div>
                </button>
              </div>
              <div class="px-3 pb-2">
                <span class="text-xs text-indigo-200">ID: {{ v.id }}</span>
                <span class="ml-2 text-xs text-indigo-200">Owner: {{ v.ownerUid }}</span>
              </div>
              <!-- Action Footer -->
              <div class="px-3 pb-3">
                <div class="w-full">
                  <div class="grid grid-cols-1 gap-0.5">
                    <button
                      class="px-2 py-1 text-white bg-indigo-700 hover:bg-indigo-600 rounded text-xs"
                      @click="removeVillager(v.id)"
                      :disabled="isUpdating"
                      :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
                    >
                      <i class="fas fa-user-times mr-1"></i> ไล่ออก
                    </button>
                  </div>
                </div>
              </div>
              <!-- เส้นไฮไลต์ขอบบนเมื่อโฮเวอร์ -->
              <div class="absolute inset-x-0 top-0 h-1 bg-indigo-600 opacity-0 group-hover:opacity-100 transition"></div>
            </div>
          </div>
        </div>
        <div v-else class="text-center text-gray-300 py-10">
          <div class="mx-auto w-16 h-16 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center mb-3">
            <i class="fas fa-users-slash text-gray-500"></i>
          </div>
          <div class="text-lg text-gray-200 mb-2">ไม่มีชาวบ้านในแคมป์</div>
          <div class="text-sm text-gray-400 mb-4">ไม่มีชาวบ้านที่ถูกต้องในแคมป์นี้</div>
        </div>
      </div>
      <!-- Max Capacity Update -->
      <div class="mb-6">
        <h4 class="text-sm font-semibold text-indigo-300 mb-3">อัปเกรดความจุสูงสุดของแคมป์</h4>
        <div class="flex items-center gap-3">
          <input
            v-model="newMaxCapacity"
            type="number"
            placeholder="ความจุสูงสุดใหม่"
            class="p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm w-40 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
          />
          <button
            @click="updateMaxCapacity"
            :disabled="isUpdating || !newMaxCapacity"
            class="btn btn-primary"
            :class="{ 'cursor-not-allowed opacity-70': isUpdating || !newMaxCapacity }"
          >
            <div class="btn-content">
              <i class="fas fa-sync-alt text-base"></i>
              <span class="text-xs font-semibold">อัปเกรดความจุ</span>
            </div>
          </button>
        </div>
      </div>
    </div>

    <!-- No Data -->
    <div
      v-else-if="!isLoading && !campData"
      class="text-center text-indigo-300 text-sm animate-fadeIn"
    >
      กรุณากรอก UID หรือเลือกตัวละครเพื่อตรวจสอบข้อมูล
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { projectFirestore, FieldValue } from '../firebase/config';
import gameVillagersData from '@/data/gameVillagers.json';

export default {
  setup() {
    const uid = ref('');
    const selectedCharacter = ref('');
    const campData = ref(null);
    const villagers = ref([]);
    const villagerIds = ref([]);
    const invalidVillagerIds = ref([]);
    const survivors = ref([]);
    const error = ref(null);
    const isLoading = ref(false);
    const isUpdating = ref(false);
    const currentIndex = ref(-1);
    const pageSize = 150;
    const lastDoc = ref(null);
    const hasMoreData = ref(true);
    const newMaxCapacity = ref(null);
    const templateCache = ref({});
    const currentTime = ref(Date.now());
    const hasDuplicates = ref(false);
    const uniqueVillagerIds = ref([]);
    const sortBy = ref('attackDice');
    let interval = null;

    // Computed property for calculated villager count
    const calculatedVillagerCount = computed(() => {
      return villagers.value.length;
    });

    // Computed property to check if villager count matches
    const isVillagerCountMatching = computed(() => {
      if (!campData.value || !campData.value.capacity) return true;
      return campData.value.capacity === calculatedVillagerCount.value;
    });

    const sortedVillagers = computed(() => {
      return [...villagers.value].sort((a, b) => {
        const levelA = getLevel(a);
        const levelB = getLevel(b);
        if (levelA !== levelB) return levelB - levelA; // desc

        let secondaryA, secondaryB;
        if (sortBy.value === 'level') {
          secondaryA = levelA;
          secondaryB = levelB;
        } else if (sortBy.value === 'attackDice' || sortBy.value === 'defenseDice') {
          secondaryA = getDice(a, sortBy.value);
          secondaryB = getDice(b, sortBy.value);
        } else {
          secondaryA = getTotalStat(a, sortBy.value);
          secondaryB = getTotalStat(b, sortBy.value);
        }
        return secondaryB - secondaryA; // desc
      });
    });

    // Fetch ranked survivors (same as inventory)
    const fetchSurvivors = async () => {
      try {
        isLoading.value = true;
        const query = projectFirestore
          .collection('characters')
          .where('rank', '==', true)
          .where('isLive', '==', true)
          .orderBy('survivalStartTime', 'asc')
          .orderBy('room', 'asc')
          .limit(pageSize);

        const snapshot = await query.get();
        survivors.value = snapshot.docs.map((doc) => ({
          docId: doc.id,
          ...doc.data(),
        }));
        lastDoc.value = snapshot.docs[snapshot.docs.length - 1];
        hasMoreData.value = snapshot.docs.length === pageSize;
        isLoading.value = false;
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการโหลดอันดับ: ' + err.message;
        isLoading.value = false;
      }
    };

    // Fetch template
    const fetchTemplate = async (name) => {
      if (templateCache.value[name]) {
        return templateCache.value[name];
      }
      const snap = await projectFirestore
        .collection('gameVillagers')
        .where('name', '==', name)
        .limit(1)
        .get();
      if (!snap.empty) {
        const t = { id: snap.docs[0].id, ...snap.docs[0].data() };
        templateCache.value[name] = t;
        return t;
      }
      return null;
    };

    // Fetch camp data by UID
    const fetchCampData = async () => {
      if (!uid.value) {
        error.value = 'กรุณากรอก UID';
        return;
      }
      selectedCharacter.value = ''; // Clear selected character
      currentIndex.value = survivors.value.findIndex((s) => s.docId === uid.value);
      isLoading.value = true;
      error.value = null;
      campData.value = null;
      villagers.value = [];
      villagerIds.value = [];
      invalidVillagerIds.value = [];
      hasDuplicates.value = false;
      uniqueVillagerIds.value = [];

      try {
        // Fetch camp data
        const campDoc = await projectFirestore
          .collection('camps')
          .doc(uid.value)
          .get();

        let capacity = 0;
        let maxCapacity = 10;
        if (campDoc.exists) {
          const campSnapData = campDoc.data();
          const campVillagers = campSnapData.villagers || {};
          const campVillagerIds = Object.keys(campVillagers);
          villagerIds.value = campVillagerIds;
          hasDuplicates.value = false;
          invalidVillagerIds.value = [];
          capacity = campVillagerIds.length;
          maxCapacity = campSnapData.capacity || 10;

          // Build villagers from V2 object map
          villagers.value = Object.entries(campVillagers).map(([id, v]) => {
            const tpl = gameVillagersData[v.templateId] || {};
            return {
              id,
              ...v,
              ...tpl,
              ...v.states,
              template: tpl,
            };
          });
        }

        campData.value = {
          capacity,
          maxCapacity,
        };
      } catch (err) {
        console.error('Error fetching camp data:', err);
        error.value = `เกิดข้อผิดพลาด: ${err.message}`;
      } finally {
        isLoading.value = false;
      }
    };

    // Fetch camp data by character selection
    const fetchCampDataByCharacter = async () => {
      if (!selectedCharacter.value) {
        campData.value = null;
        villagers.value = [];
        villagerIds.value = [];
        invalidVillagerIds.value = [];
        uid.value = '';
        currentIndex.value = -1;
        return;
      }
      uid.value = selectedCharacter.value;
      currentIndex.value = survivors.value.findIndex((s) => s.docId === selectedCharacter.value);
      await fetchCampData();
    };

    // Navigate to previous character
    const previousCharacter = async () => {
      if (currentIndex.value > 0) {
        currentIndex.value--;
        selectedCharacter.value = survivors.value[currentIndex.value].docId;
        uid.value = selectedCharacter.value;
        await fetchCampData();
      }
    };

    // Navigate to next character
    const nextCharacter = async () => {
      if (currentIndex.value < survivors.value.length - 1) {
        currentIndex.value++;
        selectedCharacter.value = survivors.value[currentIndex.value].docId;
        uid.value = selectedCharacter.value;
        await fetchCampData();
      }
    };

    // Update villager count (refresh data)
    const updateVillagerCount = async () => {
      if (!uid.value || !campData.value) return;
      isUpdating.value = true;
      error.value = null;

      try {
        await fetchCampData();
      } catch (err) {
        console.error('Error updating villager count:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเกรดจำนวนชาวบ้าน: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };

    // Remove invalid villager IDs (V2: no-op)
    const removeInvalidVillagerIds = async () => {
      // V2 object maps don't have invalid IDs in the same way as V1 arrays
      console.log('V2 schema: no invalid IDs to remove');
    };

    // Remove a specific villager from camp
    const removeVillager = async (villagerId) => {
      if (!uid.value || !villagerId) return;
      isUpdating.value = true;
      error.value = null;

      try {
        const campDocRef = projectFirestore.collection('camps').doc(uid.value);
        const campDoc = await campDocRef.get();
        if (campDoc.exists) {
          const campSnapData = campDoc.data();
          const campVillagers = campSnapData.villagers || {};
          const newVillagerCount = Math.max(0, Object.keys(campVillagers).length - 1);

          await campDocRef.update({
            [`villagers.${villagerId}`]: FieldValue.delete(),
            updatedAt: new Date(),
          });

          // Log the removal
          await projectFirestore.collection('logs').doc().set({
            uid: uid.value,
            action: 'remove_villager',
            timestamp: new Date(),
            details: {
              removedVillagerId: villagerId,
              newVillagerCount,
            },
            displayMessage: `ไล่ชาวบ้าน ID ${villagerId} ออกจากแคมป์ของผู้เล่น ${uid.value}`,
            success: true,
            error: null,
            functionName: 'removeVillager',
          });

          // Refresh data
          await fetchCampData();
        }
      } catch (err) {
        console.error('Error removing villager:', err);
        error.value = `เกิดข้อผิดพลาดในการไล่ชาวบ้าน: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };

    // Reset cooldown for a villager
    const resetCooldown = async (v) => {
      if (!v.id) return;
      isUpdating.value = true;
      error.value = null;

      try {
        await projectFirestore.collection('camps').doc(uid.value).update({
          [`villagers.${v.id}.states.lastUsedInAttackAt`]: null,
          updatedAt: new Date(),
        });

        // Log the reset
        await projectFirestore.collection('logs').doc().set({
          uid: uid.value,
          action: 'reset_villager_cooldown',
          timestamp: new Date(),
          details: {
            villagerId: v.id,
          },
          displayMessage: `รีเซ็ต cooldown ของชาวบ้าน ID ${v.id} สำหรับผู้เล่น ${uid.value}`,
          success: true,
          error: null,
          functionName: 'resetCooldown',
        });

        // Refresh data
        await fetchCampData();
      } catch (err) {
        console.error('Error resetting cooldown:', err);
        error.value = `เกิดข้อผิดพลาดในการรีเซ็ต cooldown: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };

    // Clear all villagers from camp
    const clearCamp = async () => {
      if (!uid.value || villagers.value.length === 0) return;
      if (!confirm('คุณแน่ใจหรือไม่ที่จะเคลียร์แคมป์ทั้งหมด? การกระทำนี้ไม่สามารถย้อนกลับได้')) return;
      isUpdating.value = true;
      error.value = null;

      try {
        const campDocRef = projectFirestore.collection('camps').doc(uid.value);
        const campDoc = await campDocRef.get();
        if (campDoc.exists) {
          const oldVillagers = campDoc.data().villagers || {};
          const oldVillagerCount = Object.keys(oldVillagers).length;

          await campDocRef.update({
            villagers: {},
            updatedAt: new Date(),
          });

          // Log the clear
          await projectFirestore.collection('logs').doc().set({
            uid: uid.value,
            action: 'clear_camp',
            timestamp: new Date(),
            details: {
              oldVillagerCount,
              newVillagerCount: 0,
            },
            displayMessage: `เคลียร์แคมป์ทั้งหมดของผู้เล่น ${uid.value} (ไล่ ${oldVillagerCount} ชาวบ้าน)`,
            success: true,
            error: null,
            functionName: 'clearCamp',
          });

          // Refresh data
          await fetchCampData();
        }
      } catch (err) {
        console.error('Error clearing camp:', err);
        error.value = `เกิดข้อผิดพลาดในการเคลียร์แคมป์: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };

    // Update max capacity
    const updateMaxCapacity = async () => {
      if (!uid.value || !newMaxCapacity.value) return;
      isUpdating.value = true;
      error.value = null;

      try {
        const campDocRef = projectFirestore.collection('camps').doc(uid.value);
        const oldMax = campData.value.maxCapacity;

        await campDocRef.update({
          capacity: parseInt(newMaxCapacity.value),
          updatedAt: new Date(),
        });

        campData.value.maxCapacity = parseInt(newMaxCapacity.value);
        newMaxCapacity.value = null;

        // Log the update
        await projectFirestore.collection('logs').doc().set({
          uid: uid.value,
          action: 'update_max_capacity',
          timestamp: new Date(),
          details: {
            oldMaxCapacity: oldMax,
            newMaxCapacity: campData.value.maxCapacity,
          },
          displayMessage: `อัปเกรดความจุสูงสุดของแคมป์สำหรับผู้เล่น ${uid.value}: จาก ${oldMax} เป็น ${campData.value.maxCapacity}`,
          success: true,
          error: null,
          functionName: 'updateMaxCapacity',
        });
      } catch (err) {
        console.error('Error updating max capacity:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเกรดความจุสูงสุด: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };

    const getBaseStats = (v) => {
      return v.stats || v.template?.stats || {};
    };

    const getUpgradeStats = (v) => {
      return v.upgradeStats || {};
    };

    const getTotalStat = (v, key) => {
      const base = getBaseStats(v)[key] || 0;
      const upgrade = getUpgradeStats(v)[key] || 0;
      return base + upgrade;
    };

    const getUpgradeStat = (v, key) => {
      return getUpgradeStats(v)[key] || 0;
    };

    const getDice = (v, key) => {
      return v[key] ?? v.template?.[key] ?? 0;
    };

    const getLevel = (v) => {
      return v.level ?? v.template?.level ?? 0;
    };

    const remainingMs = (v) => {
      const lastUsed = v.lastUsedInAttackAt ?? v.states?.lastUsedInAttackAt;
      if (!lastUsed) return 0;
      const last = lastUsed.toDate ? lastUsed.toDate().getTime() : new Date(lastUsed).getTime();
      const cooldown = 20 * 60 * 60 * 1000;
      const elapsed = currentTime.value - last;
      return Math.max(0, cooldown - elapsed);
    };

    const formatRemaining = (ms) => {
      if (ms <= 0) return '00:00:00';
      const hours = Math.floor(ms / 3600000);
      const minutes = Math.floor((ms % 3600000) / 60000);
      const seconds = Math.floor((ms % 60000) / 1000);
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleImageError = (event) => {
      event.target.src = 'https://via.placeholder.com/64?text=No+Image';
    };

    const fixDuplicates = async () => {
      // V2: object maps don't have duplicates
      console.log('V2 schema: no duplicates to fix');
    };

    // Initialize data
    onMounted(async () => {
      await fetchSurvivors();
      interval = setInterval(() => {
        currentTime.value = Date.now();
      }, 1000);
    });

    return {
      uid,
      selectedCharacter,
      campData,
      villagers,
      villagerIds,
      invalidVillagerIds,
      survivors,
      error,
      isLoading,
      isUpdating,
      currentIndex,
      calculatedVillagerCount,
      isVillagerCountMatching,
      newMaxCapacity,
      fetchCampData,
      fetchCampDataByCharacter,
      previousCharacter,
      nextCharacter,
      updateVillagerCount,
      removeInvalidVillagerIds,
      removeVillager,
      resetCooldown,
      clearCamp,
      updateMaxCapacity,
      getDice,
      remainingMs,
      formatRemaining,
      hasMoreData,
      hasDuplicates,
      fixDuplicates,
      sortedVillagers,
      sortBy,
      getTotalStat,
      getUpgradeStat,
      handleImageError,
    };
  },
};
</script>

<style scoped>
/* Background */
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1')
      no-repeat center center fixed,
    linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn {
  animation: fadeIn 0.3s ease-out;
}

@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

/* Font */
.font-orbitron {
  font-family: 'Orbitron', sans-serif;
}

/* Responsive */
@media (max-width: 639px) {
  .text-lg {
    font-size: 1rem;
  }
  .text-xl {
    font-size: 1.125rem;
  }
  .text-sm {
    font-size: 0.75rem;
  }
  .p-5 {
    padding: 0.75rem;
  }
}

/* Scrollbar */
::-webkit-scrollbar { width: 6px; height: 8px; }
::-webkit-scrollbar-track { background: #374151; border-radius: 10px; }
::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 10px; }
::-webkit-scrollbar-thumb:hover { background: #6b7280; }
</style>